import {
  MainPageType,
  useLastVisitedMainPage,
  useResponsiveMobile,
  useSearchParamChangeObserver,
  useSessionQueryString,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbCardContent,
  WbHeader,
  WbHeaderActions,
} from '@agilelab/plugin-wb-platform';
import {
  TaxonomySelector,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { Content, WarningPanel } from '@backstage/core-components';
import { EntityListProvider } from '@backstage/plugin-catalog-react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { BlueprintCards } from './BlueprintCards';
import { BlueprintListFilters } from './BlueprintListFilters';
import { BlueprintListTypeButtons } from './BlueprintListTypeButtons';
import { BlueprintTable } from './BlueprintTable';
import { BlueprintListContextProvider } from './context/BlueprintListContext';
import { useBlueprintListContext } from './context/useBlueprintListContext';
import { ScaffolderPageContextMenu } from '../ScaffolderPage';
import { WbPage } from '@agilelab/plugin-wb-platform';
import { useEffect } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    collapse: {
      width: '100%',
    },
    wrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2),
    },
    content: {
      '& > div:first-child': {
        margin: 0,
      },
    },
  }),
);

export const BlueprintListContent = () => {
  const { error, viewType, setViewType } = useBlueprintListContext();
  const classes = useStyles();

  const [_, setLastVisitedMainPage] = useLastVisitedMainPage(
    MainPageType.BUILDER,
  );
  setLastVisitedMainPage();

  const [_sessionQueryString, setSessionQueryString] = useSessionQueryString();
  useSearchParamChangeObserver(
    (_previousSearch: string, currentSearch: string) => {
      setSessionQueryString(currentSearch);
    },
  );

  const { shouldDisable } = useTaxonomySelection();

  const isMobile = useResponsiveMobile();

  useEffect(() => {
    if (isMobile) {
      setViewType('table');
    }
  }, [isMobile, setViewType]);

  if (error)
    return (
      <WarningPanel
        title="Oops! Something went wrong loading the templates"
        severity="error"
      >
        {error.message}
      </WarningPanel>
    );

  return (
    <WbPage themeId="home">
      <WbHeader
        titleStyle={{ display: 'flex', alignItems: 'center' }}
        pageTitleOverride="Blueprints"
        title={
          <>
            Blueprints{' '}
            <ScaffolderPageContextMenu editor={false} actions={false} />
          </>
        }
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Builder</WbBreadcrumb>
            <WbBreadcrumb>Blueprints</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      >
        <WbHeaderActions>
          {!shouldDisable && <TaxonomySelector />}
        </WbHeaderActions>
      </WbHeader>
      <Content className={classes.content}>
        <WbCard title="Available Blueprints">
          <WbCardContent>
            <Box
              style={{
                display: 'flex',
              }}
            >
              <Box style={{ flex: 1 }}>
                <BlueprintListFilters />
              </Box>
              {!isMobile && <BlueprintListTypeButtons />}
            </Box>

            {viewType === 'table' ? <BlueprintTable /> : <BlueprintCards />}
          </WbCardContent>
        </WbCard>
      </Content>
    </WbPage>
  );
};

export const BlueprintList = () => (
  <EntityListProvider>
    <BlueprintListContextProvider>
      <BlueprintListContent />
    </BlueprintListContextProvider>
  </EntityListProvider>
);
